import { clsx } from 'clsx'
import { type FC } from 'react'
import { FormattedMessage, type IntlShape, useIntl } from 'react-intl'

import {
  OfferDiscountTypeEnum,
  type OfferV11,
  type PimProductWithVariantsV1,
  type SellerPromotionV1,
} from '@redteclab/api/clients/bully'

import { Link } from 'base-ui'

import { useGlobalConfigContext } from '../../../global-config'
import { getCurrencyForLocale } from '../../../price'
import { urlResolverGetLinkToAllProductsOfSeller } from '../../../url-handling'
import { productAccessorGetSeller } from '../../model/productAccessor/productAccessorGetSeller'
import { productAccessorGetSellerPromotionFromOffer } from '../../model/productAccessor/productAccessorGetSellerPromotionFromOffer'
import { productAccessorOfferHasSellerPromotion } from '../../model/productAccessor/productAccessorOfferHasSellerPromotion'
import { productAccessorStockStatusIsAvailable } from '../../model/productAccessor/productAccessorStockStatusIsAvailable'

type ProductSellerPromotionInfoBannerSize = 's' | 'xs'

type ProductSellerPromotionInfoBannerProps = {
  className?: string
  hideSellerSerpLink?: boolean
  offer: OfferV11 | undefined
  product: PimProductWithVariantsV1
  size?: ProductSellerPromotionInfoBannerSize
}

const getFormattedPercentage = (percentage: number, intl: IntlShape): string =>
  `${intl.formatNumber(percentage * 100, { maximumFractionDigits: 2 })}%`

const getCurrencySymbolFromLocale = (locale: string): string => {
  const currencyName = getCurrencyForLocale(locale)

  switch (currencyName) {
    case 'CHF': {
      return 'CHF'
    }
    case 'EUR': {
      return '€'
    }
    default: {
      return ''
    }
  }
}

const getFormattedValueWithCurrency = (
  value: number,
  intl: IntlShape,
): string =>
  `${intl.formatNumber(value, {
    minimumFractionDigits: 0,
  })}${getCurrencySymbolFromLocale(intl.locale)}`

const getFormattedDiscountAmount = (
  sellerPromotion: SellerPromotionV1,
  intl: IntlShape,
): string => {
  switch (sellerPromotion.discountType) {
    case 'totalAmount': {
      return getFormattedValueWithCurrency(sellerPromotion.discountValue, intl)
    }
    case 'totalPercent': {
      return getFormattedPercentage(sellerPromotion.discountValue, intl)
    }
    default: {
      return ''
    }
  }
}

export const ProductSellerPromotionInfoBanner: FC<
  ProductSellerPromotionInfoBannerProps
> = ({ className, hideSellerSerpLink = false, offer, product, size = 's' }) => {
  const config = useGlobalConfigContext()
  const intl = useIntl()

  if (!offer?.seller?.sellerId) {
    return null
  }

  const productOfferHasSellerPromotion =
    productAccessorOfferHasSellerPromotion(offer)

  const seller = productAccessorGetSeller(offer)
  const sellerPromotion = productAccessorGetSellerPromotionFromOffer(offer)

  return (
    productAccessorStockStatusIsAvailable(product) &&
    productOfferHasSellerPromotion &&
    sellerPromotion && (
      <div
        className={clsx(
          'flex rounded-2xl bg-light-brand-medium p-4',
          className,
        )}
        data-qa-id="product-seller-promotion-info-banner"
      >
        <div
          className={clsx(
            'flex shrink-0 items-center justify-center rounded-full bg-light-tertiary font-mono font-medium',
            size === 'xs' && 'size-12 text-xs',
            size === 's' && 'size-20 text-xl',
          )}
          data-qa-id="product-seller-promotion-banner-badge"
        >
          {sellerPromotion.discountType === OfferDiscountTypeEnum.TotalAmount
            ? getFormattedValueWithCurrency(
                sellerPromotion.discountValue * -1,
                intl,
              )
            : null}

          {sellerPromotion.discountType === OfferDiscountTypeEnum.TotalPercent
            ? getFormattedPercentage(sellerPromotion.discountValue * -1, intl)
            : null}
        </div>
        <div className="ml-4 text-xs tablet:text-s">
          <p data-qa-id="product-seller-promotion-banner-description">
            <FormattedMessage
              id="seller.promotion.product.banner.info"
              values={{
                formattedDiscountAmount: getFormattedDiscountAmount(
                  sellerPromotion,
                  intl,
                ),
                formattedMovAmount: getFormattedValueWithCurrency(
                  // eslint-disable-next-line no-inline-comments, @typescript-eslint/no-deprecated -- Need to support legacy API until Q2 migration
                  sellerPromotion.minimumOrderValue ?? 0, // NOSONAR
                  intl,
                ),
                sellerName: seller?.displayName,
              }}
            />
          </p>
          {!hideSellerSerpLink && (
            <Link
              className="tablet:font-medium"
              data-qa-id="product-seller-promotion-banner-seller-serp-link"
              href={urlResolverGetLinkToAllProductsOfSeller(
                config,
                offer.seller.sellerId,
              )}
            >
              <FormattedMessage id="seller.promotion.product.banner.sellerSerpLink" />
            </Link>
          )}
        </div>
      </div>
    )
  )
}
